/**
 * ClassifierTab Component
 *
 * Unified tab for response classifier configuration.
 * Integrates:
 * - Classifier settings (model, thresholds, timeout)
 * - Skills configuration
 * - Response type instructions (quick/deep)
 *
 * Two main views:
 * - 响应分类: Response classification settings and prompts
 * - 技能分类: Skills configuration
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Card } from '@/components/ui/Card';
import { FieldSection } from '@/components/fields/FieldSection';
import { SkillsTab } from './SkillsTab';
import { FIELD_CONFIG, getConfigValue } from '@/config/fieldConfig';
import { FIELD_SECTIONS } from '@/config/fieldSections';
import { Filter, Zap, CheckCircle2, XCircle, ArrowRight } from 'lucide-react';
import type { AgentConfiguration } from '@/types';

// Classification Strategy Overview Component
interface ClassificationStrategyOverviewProps {
  classifierEnabled: boolean;
  ruleConfidenceThreshold: number;
  skillsEnabled: boolean;
  semanticMatchingEnabled: boolean;
  llmModelId: string;
  quickResponseModelEnabled: boolean;
}

const ClassificationStrategyOverview: React.FC<ClassificationStrategyOverviewProps> = ({
  classifierEnabled,
  ruleConfidenceThreshold,
  skillsEnabled,
  semanticMatchingEnabled,
  llmModelId,
  quickResponseModelEnabled,
}) => {
  if (!classifierEnabled) {
    return null;
  }

  const strategies = [
    {
      name: '规则匹配',
      enabled: true, // Always enabled when classifier is enabled
      description: `置信度 ≥ ${(ruleConfidenceThreshold * 100).toFixed(0)}% 时直接分类`,
      badge: '优先',
      badgeColor: 'bg-green-100 text-green-700',
    },
    {
      name: '技能匹配',
      enabled: skillsEnabled,
      description: semanticMatchingEnabled ? '规则 + 语义匹配' : '仅规则匹配',
      badge: skillsEnabled ? '启用' : '禁用',
      badgeColor: skillsEnabled ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500',
    },
    {
      name: 'LLM 兜底',
      enabled: true, // Always enabled as fallback
      description: `使用 ${llmModelId}`,
      badge: '兜底',
      badgeColor: 'bg-amber-100 text-amber-700',
    },
  ];

  return (
    <Card className="p-4 bg-slate-50 border-slate-200 mb-4">
      <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
        <Filter className="w-4 h-4" />
        当前分类策略
      </h4>
      <div className="flex items-center gap-2 flex-wrap">
        {strategies.map((strategy, index) => (
          <React.Fragment key={strategy.name}>
            <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-slate-200 shadow-sm">
              {strategy.enabled ? (
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-gray-400" />
              )}
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-700">{strategy.name}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${strategy.badgeColor}`}>
                    {strategy.badge}
                  </span>
                </div>
                <p className="text-xs text-slate-500">{strategy.description}</p>
              </div>
            </div>
            {index < strategies.length - 1 && (
              <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
            )}
          </React.Fragment>
        ))}
      </div>
      {quickResponseModelEnabled && (
        <div className="mt-2 text-xs text-slate-500 flex items-center gap-1">
          <Zap className="w-3 h-3 text-amber-500" />
          QUICK 响应将使用独立模型
        </div>
      )}
    </Card>
  );
};

// View types for the classifier tab
type ClassifierView = 'response' | 'skills';

// Classifier-related sections
const CLASSIFIER_SECTIONS = [
  'response_classifier',
  'response_classifier_prompts',
];

interface ClassifierTabProps {
  config: Partial<AgentConfiguration>;
  onChange: (name: string, value: any) => void;
  appContext: any;
  agentType: string;
}

export const ClassifierTab: React.FC<ClassifierTabProps> = ({
  config,
  onChange,
  appContext,
  agentType,
}) => {
  // Current view state
  const [currentView, setCurrentView] = useState<ClassifierView>('response');

  // Group fields by section
  const groupedFields = useMemo(() => {
    const groups: Record<string, Array<{ name: string; schema: typeof FIELD_CONFIG[string] }>> = {};

    Object.entries(FIELD_CONFIG).forEach(([name, schema]) => {
      const section = schema.section;
      if (CLASSIFIER_SECTIONS.includes(section)) {
        if (!groups[section]) {
          groups[section] = [];
        }
        groups[section].push({ name, schema });
      }
    });

    return groups;
  }, []);

  // Check if a section has any visible fields
  const sectionHasVisibleFields = useCallback((sectionKey: string) => {
    const fields = groupedFields[sectionKey];
    if (!fields || fields.length === 0) return false;

    return fields.some((field) => {
      if (!field.schema.showIf) return true;
      return field.schema.showIf(appContext);
    });
  }, [groupedFields, appContext]);

  // Sort sections by CLASSIFIER_SECTIONS order
  const sortedSections = useMemo(() => {
    return CLASSIFIER_SECTIONS
      .filter(key => FIELD_SECTIONS[key] && groupedFields[key]?.length > 0)
      .map(key => [key, FIELD_SECTIONS[key]] as [string, typeof FIELD_SECTIONS[string]]);
  }, [groupedFields]);

  // Skills configuration values
  const skills = getConfigValue(config, 'hooks.classifier.skills') || [];
  const skillsEnabled = getConfigValue(config, 'hooks.classifier.skills_enabled') ?? false;
  const semanticMatchingEnabled = getConfigValue(config, 'hooks.classifier.semantic_matching_enabled') ?? false;
  const semanticSimilarityThreshold = getConfigValue(config, 'hooks.classifier.semantic_similarity_threshold') ?? 0.75;
  const classifierEnabled = getConfigValue(config, 'hooks.classifier.enabled') ?? false;
  const classifierVisible = getConfigValue(config, 'hooks_visible.classifier') !== false;
  const ruleConfidenceThreshold = getConfigValue(config, 'hooks.classifier.rule_confidence_threshold') ?? 0.8;
  const skillLlmTimeout = getConfigValue(config, 'hooks.classifier.skill_llm_timeout') ?? 5.0;
  const skillToolInstructions = getConfigValue(config, 'hooks.classifier.skill_tool_instructions') ?? '';
  const llmModelId = getConfigValue(config, 'hooks.classifier.model_id') ?? 'gpt-5-mini';
  const quickResponseModelEnabled = getConfigValue(config, 'hooks.classifier.quick_response_model.enabled') ?? false;

  return (
    <div className="flex gap-6">
      {/* Left: Submenu */}
      <div className="w-48 flex-shrink-0">
        <div className="sticky top-14">
          <h2 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3 px-3">
            分类器配置
          </h2>
          <nav className="space-y-1">
            <button
              type="button"
              onClick={() => setCurrentView('response')}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                currentView === 'response'
                  ? 'bg-primary-50 text-primary-700 border-l-2 border-primary-500'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
            >
              <Filter className="w-4 h-4" />
              响应分类
            </button>
            <button
              type="button"
              onClick={() => setCurrentView('skills')}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                currentView === 'skills'
                  ? 'bg-primary-50 text-primary-700 border-l-2 border-primary-500'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
            >
              <Zap className="w-4 h-4" />
              技能分类
              {skills.length > 0 && (
                <span className="ml-auto px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                  {skills.length}
                </span>
              )}
            </button>
          </nav>

          {/* Help Text */}
          <div className="mt-6 px-3">
            <p className="text-xs text-slate-400">
              根据用户消息智能选择响应策略和技能
            </p>
          </div>
        </div>
      </div>

      {/* Right: Content */}
      <div className="flex-1 min-w-0">
        {/* Response Classification View */}
        {currentView === 'response' && (
          <div className="space-y-4">
            {/* Classification Strategy Overview */}
            <ClassificationStrategyOverview
              classifierEnabled={classifierEnabled}
              ruleConfidenceThreshold={ruleConfidenceThreshold}
              skillsEnabled={skillsEnabled}
              semanticMatchingEnabled={semanticMatchingEnabled}
              llmModelId={llmModelId}
              quickResponseModelEnabled={quickResponseModelEnabled}
            />

            {/* Classifier Settings */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {sortedSections.map(([sectionKey, sectionConfig]) => {
                if (!sectionHasVisibleFields(sectionKey)) {
                  return null;
                }
                return (
                  <div
                    key={sectionKey}
                    className={sectionConfig.layout === 1 ? 'xl:col-span-2' : ''}
                  >
                    <FieldSection
                      sectionKey={sectionKey}
                      sectionConfig={sectionConfig}
                      fields={groupedFields[sectionKey] || []}
                      values={appContext}
                      onChange={onChange}
                      appContext={appContext}
                    />
                  </div>
                );
              })}
            </div>

            {/* Help Text for Response Classification */}
            <Card className="p-4 bg-amber-50 border-amber-100">
              <h4 className="text-sm font-medium text-amber-800 mb-2">响应分类工作流程</h4>
              <ol className="text-xs text-amber-700 space-y-1 list-decimal list-inside">
                <li>接收用户消息后，使用规则引擎判断消息复杂度</li>
                <li>如果规则置信度低于阈值，使用LLM进行分类</li>
                <li>根据分类结果选择快速响应或深度响应指令</li>
                <li>快速响应：简洁直接，适合简单问题</li>
                <li>深度响应：详细分析，适合复杂问题</li>
              </ol>
            </Card>
          </div>
        )}

        {/* Skills Classification View */}
        {currentView === 'skills' && (
          <SkillsTab
            skills={skills}
            onSkillsChange={(newSkills) => onChange('hooks.classifier.skills', newSkills)}
            skillsEnabled={skillsEnabled}
            onSkillsEnabledChange={(enabled) => onChange('hooks.classifier.skills_enabled', enabled)}
            semanticMatchingEnabled={semanticMatchingEnabled}
            onSemanticMatchingEnabledChange={(enabled) => onChange('hooks.classifier.semantic_matching_enabled', enabled)}
            semanticSimilarityThreshold={semanticSimilarityThreshold}
            onSemanticSimilarityThresholdChange={(threshold) => onChange('hooks.classifier.semantic_similarity_threshold', threshold)}
            ruleConfidenceThreshold={ruleConfidenceThreshold}
            onRuleConfidenceThresholdChange={(threshold) => onChange('hooks.classifier.rule_confidence_threshold', threshold)}
            skillLlmTimeout={skillLlmTimeout}
            onSkillLlmTimeoutChange={(timeout) => onChange('hooks.classifier.skill_llm_timeout', timeout)}
            skillToolInstructions={skillToolInstructions}
            onSkillToolInstructionsChange={(instructions) => onChange('hooks.classifier.skill_tool_instructions', instructions)}
            classifierEnabled={classifierEnabled}
            classifierVisible={classifierVisible}
            agentContext={`Agent: ${config.name || agentType}\nDescription: ${config.description || ''}`}
          />
        )}
      </div>
    </div>
  );
};

export default ClassifierTab;
